/* Puzzle data + analysis. The analysis is a pure function of the canonical
   sentence: it indexes the letter positions, identifies the pivot (for odd-
   letter palindromes), and exposes the mirror function the cursor relies on. */

export type Difficulty = 1 | 2 | 3 | 4 | 5;

export type Puzzle = {
  id: number;
  date: string;
  sentence: string;
  hint: string;
  difficulty: Difficulty;
  maxGuesses: number;
};

export type PuzzleAnalysis = {
  sentence: string;
  /** Lowercased letters-only form. Used for scoring. */
  normalized: string;
  /** Indices into `sentence` (in order) where chars are letters. */
  letterIndices: readonly number[];
  /** Total number of letter slots. */
  letterCount: number;
  /** Letter indices marking the symmetry center: 1 element for odd-length
     palindromes, 2 (the two center letters) for even-length. */
  pivotLetterIndices: readonly number[];
  /** Maximum left-cursor position (inclusive). pivot if odd, mid-1 if even. */
  cursorMax: number;
};

const LETTER_RE = /[a-z]/i;

export function analyzePuzzle(p: Puzzle): PuzzleAnalysis {
  const indices: number[] = [];
  let normalized = "";
  for (let i = 0; i < p.sentence.length; i++) {
    const ch = p.sentence[i]!;
    if (LETTER_RE.test(ch)) {
      indices.push(i);
      normalized += ch.toLowerCase();
    }
  }

  const letterCount = indices.length;
  const reversed = normalized.split("").reverse().join("");
  if (normalized !== reversed) {
    throw new Error(`Puzzle sentence is not a palindrome: ${p.sentence}`);
  }

  const isOdd = letterCount % 2 === 1;
  const pivotLetterIndices = isOdd
    ? [(letterCount - 1) / 2]
    : [letterCount / 2 - 1, letterCount / 2];
  const cursorMax = isOdd ? pivotLetterIndices[0]! : letterCount / 2 - 1;

  return {
    sentence: p.sentence,
    normalized,
    letterIndices: indices,
    letterCount,
    pivotLetterIndices,
    cursorMax,
  };
}

/** Mirror a letter index across the palindrome center. */
export function mirrorIndex(letterIndex: number, letterCount: number): number {
  return letterCount - 1 - letterIndex;
}

/** Mask the sentence: letters → "_", everything else passes through. */
export function buildMask(sentence: string): string {
  let out = "";
  for (let i = 0; i < sentence.length; i++) {
    const ch = sentence[i]!;
    out += LETTER_RE.test(ch) ? "_" : ch;
  }
  return out;
}
